<template>
  <Teleport to="body" :disabled="!open">
    <wa-dialog light-dismiss :open="open" without-header @wa-request-close="$emit('close')">
      <div class="wishlist-create-dialog">
        <h3 class="wishlist-create-dialog__title">
          {{ $t('wishlist.dialog.createWishlist.title') }}
        </h3>
        <p class="wishlist-create-dialog__subtitle">
          {{ $t('wishlist.dialog.createWishlist.description') }}
        </p>

        <div class="wishlist-create-dialog__status">
          <div class="wishlist-create-dialog__count">
            {{
              $t('wishlist.dialog.currentCount', {
                current: wishlistsList().length,
                max: MAX_WISHLISTS
              })
            }}
          </div>

          <div v-if="numOfAvailableWishlists === 0" class="wishlist-create-dialog__limit-reached">
            {{ $t('wishlist.dialog.maxLimitReached') }}
          </div>
        </div>

        <div v-if="numOfAvailableWishlists > 0" class="wishlist-create-dialog__form">
          <wa-input
            v-model="wishlistName"
            :placeholder="$t('wishlist.dialog.newWishlistName')"
            class="wishlist-create-dialog__name-input"
            :label="$t('wishlist.dialog.wishlistName')"
            required
          />

          <div v-if="errorMessage" class="wishlist-create-dialog__error">
            {{ errorMessage }}
          </div>
        </div>
      </div>

      <wa-button slot="footer" variant="neutral" appearance="outlined" @click="$emit('close')">
        {{ $t('globals.cancel') }}
      </wa-button>
      <wa-button
        v-if="numOfAvailableWishlists > 0"
        slot="footer"
        variant="brand"
        :disabled="!wishlistName.trim()"
        :loading="creating"
        @click="createWishlist"
      >
        {{ $t('wishlist.dialog.create') }}
      </wa-button>
    </wa-dialog>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useWishlists } from '@/composables/useWishlists.js'
import { useNotification } from '@/composables/useNotification.js'
import { useCustomerStore } from '@/stores/customer'
import { useDeviceStore } from '@/stores/device'
import { useUiStore } from '@/stores/ui'
import { useI18n } from 'vue-i18n'
import { useFormatDate } from '@/composables/useFormatDate.js'
import '@awesome.me/webawesome/dist/components/dialog/dialog.js'
import '@awesome.me/webawesome/dist/components/button/button.js'
import '@awesome.me/webawesome/dist/components/input/input.js'
import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js'
const { wishlistsList, wishlistCreate, wishlistGetFreeWishlistsCount, MAX_WISHLISTS } =
  useWishlists()

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'created'])

const customerStore = useCustomerStore()
const deviceStore = useDeviceStore()
const uiStore = useUiStore()
const notification = useNotification()
const { t } = useI18n()

const wishlistName = ref('')
const creating = ref(false)
const errorMessage = ref('')

const numOfAvailableWishlists = computed(() => {
  return wishlistGetFreeWishlistsCount()
})

// Validate customer and initialize when dialog opens
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      // Check if customer is selected
      if (!customerStore.getCustomerId) {
        emit('close')
        notification.showNotification({
          message: t('wishlist.dialog.customerRequired'),
          type: 'warning'
        })
        uiStore.$patch({
          showCustomerCreationEditDialog: false,
          showCustomerSearchDialog: true
        })
        return
      }

      // Pre-fill wishlist name with default title
      const marketName = JSON.parse(localStorage.getItem('marketSettings'))
      const today = useFormatDate(new Date().toISOString().split('T')[0])
      wishlistName.value = `${marketName.name ? marketName.name : deviceStore.settings.name} ${today}`
    }
    // Reset form when dialog closes
    if (!isOpen) {
      wishlistName.value = ''
      errorMessage.value = ''
    }
  }
)

async function createWishlist() {
  if (!wishlistName.value.trim()) return

  creating.value = true
  errorMessage.value = ''

  try {
    const result = await wishlistCreate(wishlistName.value.trim())

    if (result.success) {
      emit('created', result.wishlist)
      emit('close')
    } else {
      // Handle the error - show localized message
      errorMessage.value = t(result.message)
    }
  } catch (error) {
    console.error('Failed to create wishlist:', error)
    errorMessage.value = t('wishlist.errors.createFailed')
  } finally {
    creating.value = false
  }
}
</script>

<style scoped>
.wishlist-create-dialog {
  padding-top: 10px;

  &__title {
    margin: 0 0 8px 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  &__subtitle {
    margin: 0 0 16px 0;
    color: var(--wa-color-neutral-40);
  }

  &__status {
    margin-bottom: 24px;
    padding: 16px;
    background: var(--wa-color-neutral-95);
    border-radius: var(--wa-border-radius-m);
    border: 1px solid var(--wa-color-neutral-80);
  }

  &__count {
    font-size: 0.875rem;
    color: var(--wa-color-neutral-30);
    font-weight: 500;
  }

  &__limit-reached {
    margin-top: 8px;
    padding: 12px;
    background: var(--wa-color-warning-95);
    border: 1px solid var(--wa-color-warning-80);
    border-radius: var(--wa-border-radius-s);
    color: var(--wa-color-warning-30);
    font-size: 0.875rem;
    font-weight: 500;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__name-input {
    width: 100%;
  }

  &__color-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__color-label {
    font-weight: 500;
    font-size: 0.875rem;
    color: var(--wa-color-neutral-30);
  }

  &__color-picker {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__options {
    padding-top: 8px;
    border-top: 1px solid var(--wa-color-neutral-80);
  }

  &__error {
    padding: 12px;
    background: var(--wa-color-danger-95);
    border: 1px solid var(--wa-color-danger-80);
    border-radius: var(--wa-border-radius-s);
    color: var(--wa-color-danger-30);
    font-size: 0.875rem;
    font-weight: 500;
    margin-top: 12px;
  }
}

.color-option {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 0 0 1px var(--wa-color-neutral-70);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 0 2px var(--wa-color-neutral-60);
  }

  &--selected {
    box-shadow: 0 0 0 3px var(--wa-color-brand-50);
    transform: scale(1.1);

    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-weight: bold;
      font-size: 14px;
      text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    }
  }
}
</style>
